import React, { useMemo, useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import { Drawer, Form, Input, Select, Button as AntButton } from "antd";
import { ExpandOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useMeetingPoints from "hooks/airline/useMeetingPoints";

const statusStyle = (active) =>
  active
    ? { bg: "#E6F9F0", text: "#059669", label: "Active" }
    : { bg: "#FDECEC", text: "#DC2626", label: "Inactive" };

const DetailRow = ({ label, value, isLast = false }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "170px 1fr",
      gap: 10,
      paddingBottom: isLast ? 0 : 10,
      marginBottom: isLast ? 0 : 10,
      borderBottom: isLast ? "none" : "1px solid #f1f3f5",
    }}
  >
    <span className="text-muted" style={{ fontSize: 12 }}>
      {label}
    </span>
    <span style={{ fontSize: 13, color: "#1f2937", wordBreak: "break-word" }}>
      {value || "—"}
    </span>
  </div>
);

const MeetingPointDetailsDrawer = ({ visible, item, onClose, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  React.useEffect(() => {
    if (!visible) setIsExpanded(false);
  }, [visible]);

  const ui = statusStyle(item?.status === true);

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? "large" : 600}
      zIndex={2000}
      title={null}
      closable={false}
      destroyOnHidden
    >
      <div
        style={{
          maxWidth: isExpanded ? "100%" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span>Airline Management</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {item?.name || "Meeting Point Details"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            <ExpandOutlined
              style={{ cursor: "pointer" }}
              onClick={() => setIsExpanded((prev) => !prev)}
            />
            <EditOutlined style={{ cursor: "pointer" }} onClick={() => onEdit?.(item)} />
          </div>
        </div>

        <h5 className="mb-3">{item?.name || "Meeting Point Details"}</h5>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span
            className="d-inline-flex align-items-center px-2 py-1 rounded-pill fw-medium"
            style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "0.75rem" }}
          >
            {item?.code || "—"}
          </span>
          <span
            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium"
            style={{ backgroundColor: ui.bg, color: ui.text, fontSize: "0.75rem" }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: ui.text,
              }}
            />
            {ui.label}
          </span>
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 10, textTransform: "uppercase" }}>
            Details
          </div>
          <DetailRow label="Code" value={item?.code} />
          <DetailRow label="Name" value={item?.name} />
          <DetailRow label="Description" value={item?.description} />
          <DetailRow label="ID" value={item?.id} isLast />
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fafafa", padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 10, textTransform: "uppercase" }}>
            Audit
          </div>
          <DetailRow label="Created By" value={item?.createdBy} />
          <DetailRow
            label="Created At"
            value={item?.createdAt ? dayjs(item.createdAt).format("MMM D, YYYY HH:mm") : "—"}
          />
          <DetailRow label="Updated By" value={item?.updatedBy} />
          <DetailRow
            label="Updated At"
            value={item?.updatedAt ? dayjs(item.updatedAt).format("MMM D, YYYY HH:mm") : "—"}
            isLast
          />
        </div>
      </div>
    </Drawer>
  );
};

const MeetingPointFormDrawer = ({
  open,
  onClose,
  mode = "create",
  initialValues = null,
  loading,
  onSubmit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const isEditMode = mode === "edit";

  React.useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      code: initialValues?.code || "",
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      status: initialValues?.status === false ? "inactive" : "active",
    });
  }, [open, initialValues, form]);

  React.useEffect(() => {
    if (!open) {
      setIsExpanded(false);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSave = async (values) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        description: values.description?.trim() || "",
        status: values.status !== "inactive",
      });
      form.resetFields();
      onClose();
    } catch (_error) {
      // toast handled by hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      zIndex={2000}
      placement="right"
      closable={false}
      size={isExpanded ? "large" : 600}
      onClose={onClose}
      open={open}
      destroyOnHidden
      footer={
        <div style={{ textAlign: "right", maxWidth: isExpanded ? "100%" : 700, margin: isExpanded ? 0 : "0 auto", paddingTop: 10, paddingBottom: 10 }}>
          <AntButton style={{ marginRight: 8 }} onClick={onClose} disabled={isSubmitting}>
            Cancel
          </AntButton>
          <AntButton type="primary" onClick={() => form.submit()} loading={isSubmitting || loading}>
            {isEditMode ? "Save Changes" : "Create Meeting Point"}
          </AntButton>
        </div>
      }
    >
      <div style={{ maxWidth: isExpanded ? "100%" : 700, margin: isExpanded ? 0 : "0 auto" }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div style={{ fontSize: 15, color: "#6b7280", fontFamily: "Inter" }}>
            <span>Airline Management</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {isEditMode ? "Edit Meeting Point" : "Create Meeting Point"}
            </span>
          </div>
          <ExpandOutlined
            className="task-drawer-icon"
            onClick={() => setIsExpanded((prev) => !prev)}
            style={{ cursor: "pointer" }}
          />
        </div>

        <Form layout="vertical" form={form} onFinish={handleSave} initialValues={{ status: "active" }}>
          <Form.Item label="Code" name="code" rules={[{ required: true, message: "Code is required" }]}>
            <Input placeholder="e.g. MY_CURRENT_LOCATION" maxLength={80} />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="e.g. 📍 My Current Location" maxLength={120} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Describe the meeting point..." maxLength={300} />
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

export default function MeetingPoints() {
  const { meetingPoints, loading, createMeetingPoint, updateMeetingPoint, deleteMeetingPoint } =
    useMeetingPoints();

  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessorKey: "code",
        header: "Code",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "name",
        header: "Name",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ getValue }) => getValue() || "-",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => row.status === true,
        cell: ({ getValue }) => {
          const ui = statusStyle(getValue());
          return (
            <span
              className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium"
              style={{ backgroundColor: ui.bg, color: ui.text, fontSize: "0.75rem" }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: ui.text,
                }}
              />
              {ui.label}
            </span>
          );
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <CardDropdown drop="start">
              <div className="py-2">
                <Dropdown.Item
                  onClick={() => {
                    setSelectedItem(item);
                    setShowDetailsDrawer(true);
                  }}
                >
                  View
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setFormMode("edit");
                    setSelectedItem(item);
                    setShowFormDrawer(true);
                  }}
                >
                  Edit
                </Dropdown.Item>
                <Dropdown.Divider as="div" />
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => {
                    setItemToDelete(item);
                    setShowDeleteModal(true);
                  }}
                >
                  Delete
                </Dropdown.Item>
              </div>
            </CardDropdown>
          );
        },
        meta: {
          headerProps: { className: "text-900 text-center" },
          cellProps: { className: "text-center" },
        },
      },
    ],
    [],
  );

  const table = useAdvanceTable({
    data: meetingPoints || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading && (!meetingPoints || meetingPoints.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="map-marker-alt"
        title="TravoTask Meeting Points"
        subtitle="Manage airport meeting points used for traveler pickups and directions."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <div className="d-flex justify-content-between mb-2 flex-wrap align-items-center">
          <div style={{ width: "100%", maxWidth: 300 }}>
            <AdvanceTableSearchBox placeholder="Search meeting points..." />
          </div>
          <div className="mt-3 mt-lg-0">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="plus"
              transform="shrink-3"
              onClick={() => {
                setFormMode("create");
                setSelectedItem(null);
                setShowFormDrawer(true);
              }}
              className="text-xs me-3"
            >
              <span className="ms-1">Create Meeting Point</span>
            </IconButton>
          </div>
        </div>

        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={table.getState().pagination.pageIndex}
            totalPages={table.getPageCount()}
            totalElements={table.getPrePaginationRowModel().rows.length}
            onPrev={() => table.previousPage()}
            onNext={() => table.nextPage()}
            pageSize={table.getState().pagination.pageSize}
            onPageSizeChange={(size) => {
              table.setPageSize(size);
              table.setPageIndex(0);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <MeetingPointFormDrawer
        open={showFormDrawer}
        onClose={() => setShowFormDrawer(false)}
        mode={formMode}
        initialValues={selectedItem}
        loading={loading}
        onSubmit={(payload) => {
          if (formMode === "edit" && selectedItem?.id) {
            return updateMeetingPoint(selectedItem.id, payload);
          }
          return createMeetingPoint(payload);
        }}
      />

      <MeetingPointDetailsDrawer
        visible={showDetailsDrawer}
        item={selectedItem}
        onClose={() => setShowDetailsDrawer(false)}
        onEdit={(item) => {
          setShowDetailsDrawer(false);
          setFormMode("edit");
          setSelectedItem(item);
          setShowFormDrawer(true);
        }}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        itemName="meeting point"
        loading={loading}
        onConfirm={() => deleteMeetingPoint(itemToDelete?.id)}
      />
    </>
  );
}
